import {
  Bot, BriefcaseBusiness, CloudCog, Mail, Megaphone, Search, Settings2,
  Sparkles, Wrench, Workflow, PanelsTopLeft
} from "lucide-react";

export const site = {
  name: "TPeck Digital",
  url: import.meta.env.VITE_SITE_URL || "https://www.tpeckdigital.com",
  promise: "Modern websites, smarter business tools, and complete digital support for growing businesses.",
  email: "hello@tpeckdigital.com",
  phone: "(609) 289-4939",
  location: "Serving growing businesses without geographic limits",
  consultationLabel: "Schedule a Digital Solutions Consultation"
};

export type Service = {
  slug: string;
  name: string;
  shortName: string;
  eyebrow: string;
  summary: string;
  outcome: string;
  features: string[];
  process: string[];
  idealFor: string[];
  faqs: { q: string; a: string }[];
  icon: typeof PanelsTopLeft;
};

export const services: Service[] = [
  {
    slug: "website-solutions",
    name: "Website Design & Modernization",
    shortName: "Website Solutions",
    eyebrow: "Build, redesign, and improve",
    summary: "Create a professional website or modernize an outdated one with responsive design, clearer content, stronger performance, accessibility, and search visibility built in.",
    outcome: "A modern website that represents the business clearly, works across devices, and gives visitors an easy path to become customers.",
    features: [
      "New website design and development",
      "Website redesign and modernization",
      "Mobile-responsive layouts",
      "Content organization and page planning",
      "Performance and accessibility improvements",
      "Contact forms, analytics, and integrations",
      "SEO-preserving website migrations",
      "Ongoing website maintenance"
    ],
    process: ["Understand", "Plan", "Design", "Build", "Launch", "Support"],
    idealFor: ["Businesses launching their first professional site", "Organizations with an outdated or difficult-to-manage website", "Companies that need a clearer online sales and lead-generation path"],
    faqs: [
      { q: "Can you modernize our existing website without losing all of its content?", a: "Yes. We review the current pages, preserve useful content and search equity, improve weak areas, and migrate the site through a controlled launch process." },
      { q: "Will the website work on phones and tablets?", a: "Yes. Every website is designed responsively and tested across common screen sizes and browsers." }
    ],
    icon: PanelsTopLeft
  },
  {
    slug: "hosting-email",
    name: "Managed Hosting & Business Email",
    shortName: "Hosting & Email",
    eyebrow: "One dependable home for your business",
    summary: "Keep the website, domain, security, backups, and professional email coordinated through one support relationship instead of managing disconnected vendors.",
    outcome: "A reliable online foundation with business email included in every hosting package and practical help when something needs attention.",
    features: [
      "Managed website hosting",
      "Business email included with hosting",
      "SSL certificate configuration",
      "Domain and DNS support",
      "Website backups and recovery",
      "Security and software updates",
      "Performance and uptime monitoring",
      "Email setup and migration assistance"
    ],
    process: ["Review", "Configure", "Migrate", "Protect", "Monitor", "Support"],
    idealFor: ["Businesses tired of coordinating multiple providers", "Teams that need professional email attached to their domain", "Owners who want ongoing technical support instead of self-managing hosting"],
    faqs: [
      { q: "Is business email included?", a: "Yes. Business email is included with every TPeck Digital hosting package. The number of mailboxes and storage level are defined in the selected package." },
      { q: "Can you move our current website and email?", a: "Yes. Migration planning covers the website, DNS, mailboxes, security certificates, and a cutover approach designed to minimize disruption." }
    ],
    icon: CloudCog
  },
  {
    slug: "seo-online-visibility",
    name: "SEO & Online Visibility",
    shortName: "SEO",
    eyebrow: "Help the right customers find you",
    summary: "Improve how search engines understand the business and how potential customers discover, evaluate, and contact it online.",
    outcome: "A stronger search foundation supported by useful content, sound technical implementation, and measurable visibility improvements.",
    features: [
      "Website and SEO audits",
      "Keyword and customer-intent research",
      "On-page and technical SEO",
      "Local search optimization",
      "Google Business Profile guidance",
      "Search-friendly content improvements",
      "Analytics and Search Console setup",
      "Ongoing performance reporting"
    ],
    process: ["Audit", "Research", "Prioritize", "Optimize", "Measure", "Improve"],
    idealFor: ["Growing businesses that depend on local or organic discovery", "Websites with weak rankings or unclear content", "Companies that need better visibility into what is driving traffic and leads"],
    faqs: [
      { q: "How quickly will SEO produce results?", a: "SEO is an ongoing improvement process. Technical fixes may help quickly, while meaningful ranking and traffic gains usually build over time based on competition, content, authority, and consistency." },
      { q: "Do you guarantee first-place rankings?", a: "No responsible provider can guarantee a specific ranking. TPeck Digital focuses on sound practices, useful content, technical quality, and measurable progress." }
    ],
    icon: Search
  },
  {
    slug: "digital-marketing",
    name: "Digital Marketing, Social Media & Advertising",
    shortName: "Digital Marketing",
    eyebrow: "Reach, engage, and convert",
    summary: "Connect website content, social media, advertising, landing pages, and reporting into campaigns that support the business instead of creating more disconnected work.",
    outcome: "A coordinated digital marketing presence with clearer messaging, consistent execution, and visibility into campaign performance.",
    features: [
      "Digital marketing planning",
      "Social media setup and support",
      "Social media content and graphics",
      "Google, Facebook, and Instagram campaigns",
      "Campaign landing pages",
      "Email marketing support",
      "Lead-generation and conversion tracking",
      "Campaign reporting and optimization"
    ],
    process: ["Define", "Target", "Create", "Launch", "Measure", "Refine"],
    idealFor: ["Businesses ready to generate more awareness or leads", "Teams that need coordinated website and campaign support", "Owners who want useful reporting rather than disconnected platform statistics"],
    faqs: [
      { q: "Is advertising spend included in management fees?", a: "Advertising media spend is normally paid directly to the selected platform and kept separate from campaign setup and management fees." },
      { q: "Can you help with only part of our marketing?", a: "Yes. Services can support a specific campaign, landing page, social channel, or ongoing digital marketing program." }
    ],
    icon: Megaphone
  },
  {
    slug: "ai-automation",
    name: "Custom AI Agents & Workflow Automation",
    shortName: "AI & Automation",
    eyebrow: "Practical tools that reduce repetitive work",
    summary: "Use generative AI and workflow automation where they can improve customer service, organize information, qualify leads, or simplify routine business processes.",
    outcome: "A focused automation or AI capability that solves a defined business problem while keeping people in control of important decisions.",
    features: [
      "Website and customer-service assistants",
      "Frequently asked question agents",
      "Lead qualification and follow-up workflows",
      "Internal knowledge assistants",
      "Document and information processing",
      "Form, notification, and approval automation",
      "Connections between common business tools",
      "Human review and escalation controls"
    ],
    process: ["Identify", "Simplify", "Prototype", "Validate", "Deploy", "Monitor"],
    idealFor: ["Businesses spending too much time on repetitive communication", "Teams with manual handoffs and follow-up gaps", "Companies that want to explore AI through a focused, useful starting point"],
    faqs: [
      { q: "Do we need a large technology team to use AI?", a: "No. A focused AI assistant or automation can be designed around existing tools and a clearly defined process. The right starting point is usually small and measurable." },
      { q: "Will AI replace our employees?", a: "The focus is on assisting people, reducing repetitive effort, and improving responsiveness. Important business decisions should retain appropriate human review." }
    ],
    icon: Bot
  },
  {
    slug: "business-tools",
    name: "Business Templates & Digital Tools",
    shortName: "Business Tools",
    eyebrow: "Replace outdated files with tools that work",
    summary: "Revamp spreadsheets, forms, trackers, proposals, project planners, and business templates so they are easier to use, more professional, and better aligned with current operations.",
    outcome: "Modern, branded business tools that reduce manual confusion and make important information easier to track and use.",
    features: [
      "Modernized Excel and spreadsheet tools",
      "Project planners and trackers",
      "Sales and customer tracking tools",
      "Estimate and proposal templates",
      "Dashboards and management reports",
      "Digital forms and checklists",
      "Branded document templates",
      "Lightweight custom business applications"
    ],
    process: ["Review", "Simplify", "Structure", "Automate", "Brand", "Deliver"],
    idealFor: ["Businesses relying on outdated or inconsistent templates", "Teams that need better tracking without a large software implementation", "Owners who want professional tools tailored to how the business actually works"],
    faqs: [
      { q: "Do you sell prebuilt templates through the website?", a: "The website focuses on custom template modernization and digital tools tailored to the business rather than operating a general product storefront." },
      { q: "Can you improve an existing spreadsheet instead of replacing it?", a: "Yes. Existing files can be reviewed, cleaned up, branded, automated, and expanded while retaining the parts that already work." }
    ],
    icon: BriefcaseBusiness
  }
];

export const packages = [
  {
    name: "Website Launch",
    audience: "For a growing business establishing a professional online presence.",
    includes: ["Responsive business website", "Managed hosting", "Business email", "SSL and domain configuration", "Foundational SEO", "Contact and analytics setup", "Launch support"],
    note: "Quoted based on page count, content, integrations, and design requirements."
  },
  {
    name: "Website Modernization",
    audience: "For a business with an outdated, slow, or difficult-to-manage website.",
    includes: ["Current-site review", "Modern responsive redesign", "Content and navigation improvements", "SEO-preserving migration", "Performance and accessibility improvements", "Hosting and business email", "Post-launch support"],
    note: "Quoted after reviewing the current website and migration requirements."
  },
  {
    name: "Digital Growth",
    audience: "For a business ready to improve visibility, lead generation, and ongoing marketing.",
    includes: ["Website support", "Hosting and business email", "Ongoing SEO", "Content and landing pages", "Social media support", "Advertising campaign management", "Analytics and reporting"],
    note: "Structured as a monthly service based on campaign scope and support level."
  },
  {
    name: "Complete Business Digital Support",
    audience: "For a business that wants one partner across its website and broader digital operations.",
    includes: ["Website, hosting, and email", "SEO and digital marketing", "Social media and campaigns", "Workflow automation", "Custom AI agents", "Business templates and digital tools", "Ongoing technical support"],
    note: "Designed as a tailored service plan after a digital solutions consultation."
  }
];

export const processSteps = [
  ["01", "Listen", "Understand the business, customers, current tools, pain points, and goals."],
  ["02", "Recommend", "Outline a practical solution, clear priorities, responsibilities, and costs."],
  ["03", "Build", "Design, configure, and develop the agreed digital solution."],
  ["04", "Launch", "Test, train, migrate, and put the solution into use with a controlled transition."],
  ["05", "Support", "Maintain, measure, improve, and help the business as its needs evolve."]
] as const;

export const valuePoints = [
  [Wrench, "One digital partner", "Coordinate the website, hosting, email, visibility, marketing, automation, and business tools through one relationship."],
  [Settings2, "Built around the business", "Recommendations start with the way the business operates—not with a predetermined platform or generic package."],
  [Sparkles, "Modern without being complicated", "Use current technology, AI, and automation where they add practical value while keeping the solution understandable."],
  [Mail, "Support after launch", "Hosting, business email, maintenance, and ongoing digital support keep the relationship useful after the initial project."]
] as const;
